import { FormLabel } from '@/components/FormLabel'
import { dateTimeFormat } from '@/constants/dateFormat'
import { FormRow } from '@/containers/forms/FormRow'
import { FormSection } from '@/containers/forms/FormSection'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { Apps } from '@/dtos'
import { useApp } from '@/hooks/useApp'
import { Box, Button, Collapse, Flex, Grid, Heading, Stack, Text, useDisclosure } from '@chakra-ui/react'
import { formatDate } from 'BrokerTsLib/src/Utils/formatPrice'
import type { InsuranceParamsDto } from 'BrokerTsLib/src/dtos'
import React from 'react'

type GridItemProps = {
  label: string
  value?: string | React.ReactNode
}

const GridItem = ({ label, value }: GridItemProps) => {
  return (
    <Grid>
      <FormLabel>{label}</FormLabel>
      <Text>{value}</Text>
    </Grid>
  )
}

const PoleasImportSummary = () => {
  const { app } = useApp()
  const isPublicPoleas = app == Apps.publicPoleas
  const { insuranceParams: ip } = useInsuranceParams()
  const insuranceParams = ip as InsuranceParamsDto

  const { isOpen: isOpenImportInfo, onToggle: onToggleImportInfo } = useDisclosure()
  const { isOpen: isOpenLog, onToggle: onToggleLog } = useDisclosure()

  if (!insuranceParams?.importedData || isPublicPoleas)
    return null

  const data = insuranceParams.importedData

  const name = `${data.title ? data.title + ' ' : ''}${data.firstName} ${data.lastName}`.trim()
  let nameCorresp = undefined
  if (data.titleCorresp || data.firstNameCorresp || data.lastNameCorresp) {
    nameCorresp = `${data.titleCorresp ?? ''} ${data.firstNameCorresp} ${data.lastNameCorresp}`.trim()
  }
  let rcIcoTitle = ''
  let rcIcoValue = ''
  let nameOrCompany = ''
  if (data.rc) {
    if (data.ico) {
      rcIcoTitle = 'Rč/Ič'
      rcIcoValue = `${data.rc}/${data.ico}`
      nameOrCompany = `${name}/${data.companyName}`
    }
    else {
      rcIcoTitle = 'Rč'
      rcIcoValue = data.rc
      nameOrCompany = name
    }
  }
  else {
    rcIcoTitle = 'Ič'
    rcIcoValue = data.ico ?? ''
    nameOrCompany = data.companyName ?? ''
  }

  const logLines = data.logText?.split(/\r?\n/)

  return (
    <Stack>
      <Flex justify='center' alignItems='center' sx={{ pt: { base: 0, md: 8 } }} gap={4}>
        <Button variant='link' onClick={onToggleImportInfo}>Informace o importu</Button>
        <Button variant='link' onClick={onToggleLog}>Protokol práce s nabídkou</Button>
      </Flex>

      <Collapse in={isOpenImportInfo}>
        <FormSection
          cardHeaderProps={{ ml: 16 }}
          sectionTitle='PoLeas nabídka'
          sx={{ mt: { base: 0, md: 2 } }}
        >

          {!!data.importedDate &&
            <>
              <FormRow itemsCount={3} sx={{ marginBottom: 4 }} >
                <GridItem label='Datum importu do systému' value={formatDate(new Date(data.importedDate!), dateTimeFormat)} />
                <GridItem label='Číslo leasingové smlouvy' value={data.leasingNumber} />
              </FormRow>

              <Heading sx={{ fontSize: 'lg', fontWeight: 'bold', marginBottom: 1 }}>Klient</Heading>
              <FormRow itemsCount={3} sx={{ marginBottom: 4, rowGap: 2 }} >
                <GridItem label={data.companyName ? 'Název' : 'Jméno a příjmení'} value={nameOrCompany} />
                <GridItem label={rcIcoTitle} value={rcIcoValue} />
                <GridItem label='Adresa (trvalá)' value={[data.street, data.city, data.zip].join(', ')} />
              </FormRow>

              <Heading sx={{ fontSize: 'lg', fontWeight: 'bold', marginBottom: 1 }}>Kontakt</Heading>
              <FormRow itemsCount={3} sx={{ marginBottom: 4, rowGap: 2 }}>
                {nameCorresp &&
                  <GridItem label='Jméno a příjmení' value={nameCorresp} />
                }
                <GridItem label='Telefon' value={data.contactPhone} />
                <GridItem label='Email' value={data.contactEmail} />
                <GridItem label='Adresa (korespondenční)' value={[data.streetCorresp, data.cityCorresp, data.zipCorresp].join(', ')} />
              </FormRow>

              <Heading sx={{ fontSize: 'lg', fontWeight: 'bold', marginBottom: 1 }}>Vozidlo</Heading>
              <FormRow itemsCount={3} sx={{ marginBottom: 4, rowGap: 2 }}>
                <GridItem label='VIN' value={data.vin} />
                <GridItem label='Značka' value={data.make} />
                <GridItem label='Model' value={data.model} />
                <GridItem label='Specifikace' value={data.modelSpecification} />
                <GridItem label='Objem' value={data.volume} />
                <GridItem label='Výkon' value={data.power} />
                <GridItem label='Druh, palivo' value={`${data.category}, ${data.fuel}`} />
                <GridItem label='Hmotnost' value={data.maxWeight} />
                <GridItem label='Vyrobeno' value={data.firstRegistrationYear} />
                <GridItem label='Počet míst' value={data.seatsCount} />
                <GridItem label='Reg. značka' value={data.registrationMark} />
                <GridItem label='Číslo TP' value={data.technicalCertificateNumber} />
              </FormRow>

              <Heading sx={{ fontSize: 'lg', fontWeight: 'bold', marginBottom: 1 }}>Předchozí pojištění</Heading>
              <FormRow itemsCount={3} sx={{ marginBottom: 4, rowGap: 2 }}>
                <GridItem label='Počátek' value={data.prevInsuredFrom} />
                <GridItem label='Konec' value={data.prevInsuredTo} />
                <GridItem label='Pojistné POV' value={data.prevPovAmount} />
                <GridItem label='Pojišťovna POV' value={data.prevInsurerPov} />
                <GridItem label='Číslo smlouvy POV' value={data.prevContractNumber} />
                <GridItem label='Pojistné HAV' value={data.prevHavAmount} />
                <GridItem label='Pojišťovna HAV' value={data.prevInsurerHav} />
                <GridItem label='Kód sazby HAV' value={data.prevHavKodSazby} />
                <GridItem label='Spoluúčast HAV' value={data.prevSpol} />
              </FormRow>
            </>
          }

          {!data.importedDate &&
            <Text>Data nebyla importována</Text>
          }
        </FormSection>
      </Collapse>

      <Collapse in={isOpenLog}>
        <FormSection
          cardHeaderProps={{ ml: 16 }}
          sectionTitle='Protokol práce s nabídkou'
          sx={{ mt: { base: 0, md: 2 } }}
        >
          <Box>
            {logLines && logLines.map((line, index) => {
              return (
                <Text key={index} sx={{ fontSize: 'small' }}>{line}</Text>
              )
            })
            }
          </Box>
        </FormSection>
      </Collapse>
    </Stack >
  )
}

export default PoleasImportSummary
